import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { FaTimes, FaInfinity, FaBookOpen, FaUserTie, FaSpinner } from 'react-icons/fa';
import { paymentAPI } from '../../utils/api';
import { trackCTAClick } from '../../utils/analytics';
import './DailyLimitUpgradeModal.css';

/**
 * DailyLimitUpgradeModal — donusumSonuc.md (15 May 2026)
 *
 * Login kullanici gunluk 3 sorgu hakkini bitirdiginde gosterilir.
 * Iki opsiyon sunar:
 *   1) 1 hafta sinirsiz erisim (PayPal — €3.99 default, admin'den ayarlanir)
 *   2) Ebook veya consulting satin al (1 ay sinirsiz erisim)
 */
const DailyLimitUpgradeModal = ({ isOpen, onClose, upgradeOptions }) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  const [pkg, setPkg] = useState(null);
  const [loading, setLoading] = useState(false);
  const [purchasing, setPurchasing] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    const lang = (i18n.language || 'tr').split('-')[0];
    paymentAPI.getUnlimitedPackages(lang)
      .then(result => {
        if (cancelled) return;
        // api sarmalayıcısı { success, data } döner; paketler data.packages'ta.
        const weekly = result?.success
          ? (result.data?.packages || []).find(p => p.id === 'unlimited_weekly')
          : null;
        if (weekly) {
          setPkg(weekly);
        } else if (upgradeOptions?.weeklyUnlimited) {
          // backend payload'undan fallback
          const u = upgradeOptions.weeklyUnlimited;
          setPkg({
            id: u.packageId || 'unlimited_weekly',
            price: u.price,
            currency: u.currency,
            days: u.days,
            name: t('dailyLimit.weekly.name', '1 Hafta Sınırsız'),
            description: t('dailyLimit.weekly.desc', '7 gün boyunca tüm AI özelliklerini sınırsız kullan')
          });
        } else {
          setError(result?.error?.response?.data?.message || result?.error?.message || 'Paket bilgisi alınamadı');
        }
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [isOpen, i18n.language, upgradeOptions, t]);

  if (!isOpen) return null;

  const handleBuyWeekly = async () => {
    if (!pkg) return;
    setPurchasing(true);
    setError(null);
    try {
      const origin = window.location.origin;
      // api sarmalayıcısı { success, data } döner ve HTTP hatalarını yutar
      // (throw etmez) — bu yüzden success/data kontrolü zorunlu.
      const result = await paymentAPI.createUnlimitedOrder(
        pkg.id,
        `${origin}/payment/success?type=unlimited`,
        `${origin}/payment/cancel?type=unlimited`
      );
      if (result.success && result.data?.approvalUrl) {
        window.location.href = result.data.approvalUrl;
      } else if (result.success) {
        setError(t('dailyLimit.errorNoUrl', 'PayPal yönlendirmesi alınamadı'));
      } else {
        setError(result.error?.response?.data?.message || result.error?.message || t('dailyLimit.errorPay', 'Ödeme başlatılamadı'));
      }
    } catch (err) {
      setError(err?.data?.message || err?.message || t('dailyLimit.errorPay', 'Ödeme başlatılamadı'));
    } finally {
      setPurchasing(false);
    }
  };

  // Hub yönlendirmeleri: /shop ve /consulting eski sayfa linkleri —
  // ana sayfa hub'ındaki paneller açılır (openCard ile aynı görünüm).
  const handleGoShop = () => { onClose(); navigate('/?open=library'); };
  const handleGoConsulting = () => { onClose(); navigate('/?open=consult'); };

  const priceLabel = pkg ? `${Number(pkg.price).toFixed(2)} ${pkg.currency || 'EUR'}` : '—';
  const daysLabel = pkg ? pkg.days : 7;

  return (
    <div className="dlu-overlay" onClick={onClose}>
      <div className="dlu-modal" onClick={(e) => e.stopPropagation()}>
        <button type="button" className="dlu-close" onClick={onClose} aria-label={t('dailyLimit.close', 'Kapat')}>
          <FaTimes />
        </button>

        <div className="dlu-header">
          <h2 className="dlu-title">
            {t('dailyLimit.title', 'Günlük kullanım hakkın bitti')}
          </h2>
          <p className="dlu-subtitle">
            {t('dailyLimit.subtitle', 'Sınırsız erişim için bir paket seç:')}
          </p>
        </div>

        {error && <div className="dlu-error">{error}</div>}

        <div className="dlu-options">
          {/* Option 1: Weekly unlimited */}
          <div className="dlu-option dlu-option-primary">
            <div className="dlu-option-icon"><FaInfinity /></div>
            <div className="dlu-option-body">
              <h3 className="dlu-option-title">
                {t('dailyLimit.option1.title', '1 Hafta Sınırsız Erişim')}
              </h3>
              <p className="dlu-option-desc">
                {t('dailyLimit.option1.desc', { days: daysLabel, defaultValue: `${daysLabel} gün boyunca tüm AI özelliklerini sınırsız kullan` })}
              </p>
              <div className="dlu-price">{loading ? '…' : priceLabel}</div>
              <button
                type="button"
                className="dlu-cta"
                onClick={() => { trackCTAClick('daily_limit_buy_weekly', window.location.pathname, { price: pkg?.price }); handleBuyWeekly(); }}
                disabled={loading || purchasing || !pkg}
              >
                {purchasing ? <><FaSpinner className="dlu-spin" /> {t('dailyLimit.processing', 'İşleniyor…')}</> : t('dailyLimit.option1.cta', 'Hemen Satın Al')}
              </button>
            </div>
          </div>

          {/* Option 2: Ebook or Consulting → 1 ay unlimited */}
          <div className="dlu-option">
            <div className="dlu-option-icon dlu-option-icon-alt"><FaBookOpen /></div>
            <div className="dlu-option-body">
              <h3 className="dlu-option-title">
                {t('dailyLimit.option2.title', 'E-kitap veya Danışmanlık Satın Al')}
              </h3>
              <p className="dlu-option-desc">
                {t('dailyLimit.option2.desc', '1 ay boyunca sınırsız erişim hediye olarak gelir.')}
              </p>
              <div className="dlu-actions-row">
                <button type="button" className="dlu-cta-secondary" onClick={() => { trackCTAClick('daily_limit_goto_shop', window.location.pathname); handleGoShop(); }}>
                  <FaBookOpen /> {t('dailyLimit.option2.shop', 'E-kitaplar')}
                </button>
                <button type="button" className="dlu-cta-secondary" onClick={() => { trackCTAClick('daily_limit_goto_consulting', window.location.pathname); handleGoConsulting(); }}>
                  <FaUserTie /> {t('dailyLimit.option2.consulting', 'Danışmanlık')}
                </button>
              </div>
            </div>
          </div>
        </div>

        <button type="button" className="dlu-later" onClick={() => { trackCTAClick('daily_limit_later', window.location.pathname); onClose(); }}>
          {t('dailyLimit.later', 'Yarın tekrar denerim')}
        </button>
      </div>
    </div>
  );
};

export default DailyLimitUpgradeModal;
